// GlobalSearch.tsx — Recherche globale (locataires, biens, baux)
import { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store/slices/store';
import { fetchTenants } from '../../store/slices/tenantSlice';
import { fetchLocals } from '../../store/slices/localSlice';
import { fetchLeases } from '../../store/slices/leaseSlice';

interface SearchResult {
  id: number;
  type: 'tenant' | 'local' | 'lease';
  label: string;
  sublabel: string;
  link: string;
}

const ICONS = {
  tenant: (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  ),
  local: (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  ),
  lease: (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
};

const TYPE_LABELS: Record<string, string> = {
  tenant: 'Locataires',
  local: 'Biens',
  lease: 'Baux',
};

const TYPE_COLORS: Record<string, string> = {
  tenant: 'text-blue-600 bg-blue-50',
  local: 'text-green-600 bg-green-50',
  lease: 'text-purple-600 bg-purple-50',
};

const normalize = (s: string) =>
  s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const GlobalSearch: React.FC<{ inputRef?: React.RefObject<HTMLInputElement | null> }> = ({ inputRef }) => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const tenants = useSelector((state: RootState) => (state as any).tenant?.tenants || []);
  const locals  = useSelector((state: RootState) => (state as any).local?.locals   || []);
  const leases  = useSelector((state: RootState) => state.lease?.leases            || []);

  const [query, setQuery] = useState('');
  const [open, setOpen]   = useState(false);
  const [cursor, setCursor] = useState(-1);
  const wrapRef = useRef<HTMLDivElement>(null);
  const localInputRef = useRef<HTMLInputElement>(null);
  const resolvedInputRef = inputRef || localInputRef;

  // Charge les données si pas encore en store
  useEffect(() => {
    if (!tenants.length) dispatch(fetchTenants());
    if (!locals.length)  dispatch(fetchLocals());
    if (!leases.length)  dispatch(fetchLeases());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fermeture au clic dehors
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        setCursor(-1);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Fermeture Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); setQuery(''); setCursor(-1); resolvedInputRef.current?.blur(); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const results = useMemo((): SearchResult[] => {
    if (query.trim().length < 2) return [];
    const q = normalize(query.trim());

    const tenantResults: SearchResult[] = tenants
      .filter((t: any) =>
        normalize(`${t.firstname || ''} ${t.lastname || ''} ${t.email || ''} ${t.phone || ''}`).includes(q)
      )
      .slice(0, 4)
      .map((t: any) => ({
        id: t.id,
        type: 'tenant' as const,
        label: `${t.civility || ''} ${t.firstname || ''} ${t.lastname || ''}`.trim(),
        sublabel: t.email || t.phone || '',
        link: `/Tenant/TenantHome`,
      }));

    const localResults: SearchResult[] = locals
      .filter((l: any) =>
        normalize(`${l.name || ''} ${l.street || ''} ${l.city || ''} ${l.zip || ''}`).includes(q)
      )
      .slice(0, 4)
      .map((l: any) => ({
        id: l.id,
        type: 'local' as const,
        label: l.name || l.street || `Bien #${l.id}`,
        sublabel: [l.street, l.zip, l.city].filter(Boolean).join(' · '),
        link: `/Local/LocalHome`,
      }));

    const leaseResults: SearchResult[] = leases
      .filter((l: any) => {
        const tenantName = `${l.tenant?.firstname || ''} ${l.tenant?.lastname || ''}`;
        const localName  = l.local?.name || l.local?.street || '';
        return normalize(`${tenantName} ${localName} bail ${l.id}`).includes(q);
      })
      .slice(0, 4)
      .map((l: any) => ({
        id: l.id,
        type: 'lease' as const,
        label: `Bail #${l.id} — ${l.tenant?.lastname || ''}`,
        sublabel: `${l.local?.name || l.local?.street || ''} · ${l.rents?.[l.rents.length - 1]?.amount || ''}€`,
        link: `/Rent/RentHome`,
      }));

    return [...tenantResults, ...localResults, ...leaseResults];
  }, [query, tenants, locals, leases]);

  // Grouper par type
  const grouped = useMemo(() => {
    const groups: Record<string, SearchResult[]> = {};
    results.forEach(r => {
      if (!groups[r.type]) groups[r.type] = [];
      groups[r.type].push(r);
    });
    return groups;
  }, [results]);

  const flatResults = results; // pour navigation clavier

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor(c => Math.min(c + 1, flatResults.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setCursor(c => Math.max(c - 1, 0)); }
    if (e.key === 'Enter' && cursor >= 0) {
      navigate(flatResults[cursor].link);
      setOpen(false); setQuery(''); setCursor(-1);
    }
  };

  const handleSelect = (r: SearchResult) => {
    navigate(r.link);
    setOpen(false); setQuery(''); setCursor(-1);
    resolvedInputRef.current?.blur();
  };

  return (
    <div ref={wrapRef} className="relative w-full xl:w-[430px]">
      {/* Input */}
      <div className="relative">
        <span className="absolute -translate-y-1/2 pointer-events-none left-4 top-1/2">
          <svg className="fill-gray-500 dark:fill-gray-400" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path fillRule="evenodd" clipRule="evenodd"
              d="M3.04175 9.37363C3.04175 5.87693 5.87711 3.04199 9.37508 3.04199C12.8731 3.04199 15.7084 5.87693 15.7084 9.37363C15.7084 12.8703 12.8731 15.7053 9.37508 15.7053C5.87711 15.7053 3.04175 12.8703 3.04175 9.37363ZM9.37508 1.54199C5.04902 1.54199 1.54175 5.04817 1.54175 9.37363C1.54175 13.6991 5.04902 17.2053 9.37508 17.2053C11.2674 17.2053 13.003 16.5344 14.357 15.4176L17.177 18.238C17.4699 18.5309 17.9448 18.5309 18.2377 18.238C18.5306 17.9451 18.5306 17.4703 18.2377 17.1774L15.418 14.3573C16.5365 13.0033 17.2084 11.2669 17.2084 9.37363C17.2084 5.04817 13.7011 1.54199 9.37508 1.54199Z"
              fill=""
            />
          </svg>
        </span>
        <input
          ref={resolvedInputRef}
          type="text"
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); setCursor(-1); }}
          onFocus={() => { if (query.length >= 2) setOpen(true); }}
          onKeyDown={handleKeyDown}
          placeholder="Rechercher locataire, bien, bail…"
          className="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-200 bg-transparent py-2.5 pl-12 pr-14 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-800 dark:bg-gray-900 dark:bg-white/[0.03] dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
        />
        {query ? (
          <button
            onClick={() => { setQuery(''); setOpen(false); setCursor(-1); }}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 rounded"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        ) : (
          <button className="absolute right-2.5 top-1/2 inline-flex -translate-y-1/2 items-center gap-0.5 rounded-lg border border-gray-200 bg-gray-50 px-[7px] py-[4.5px] text-xs -tracking-[0.2px] text-gray-500 dark:border-gray-800 dark:bg-white/[0.03] dark:text-gray-400">
            <span>⌘</span><span>K</span>
          </button>
        )}
      </div>

      {/* Dropdown résultats */}
      {open && query.length >= 2 && (
        <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg z-[9999] overflow-hidden">
          {results.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-gray-400">
              Aucun résultat pour « {query} »
            </div>
          ) : (
            <div className="py-1 max-h-[380px] overflow-y-auto">
              {(['tenant', 'local', 'lease'] as const).map(type => {
                const group = grouped[type];
                if (!group?.length) return null;
                return (
                  <div key={type}>
                    {/* En-tête de section */}
                    <div className="px-3 pt-2 pb-1">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                        {TYPE_LABELS[type]}
                      </span>
                    </div>
                    {group.map(r => {
                      const flatIdx = flatResults.indexOf(r);
                      const isActive = cursor === flatIdx;
                      return (
                        <button
                          key={`${r.type}-${r.id}`}
                          onClick={() => handleSelect(r)}
                          className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                            isActive ? 'bg-gray-100' : 'hover:bg-gray-50'
                          }`}
                        >
                          <span className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${TYPE_COLORS[type]}`}>
                            {ICONS[type]}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{r.label}</p>
                            {r.sublabel && (
                              <p className="text-xs text-gray-400 truncate">{r.sublabel}</p>
                            )}
                          </div>
                          <svg className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
              <div className="px-3 py-2 border-t border-gray-100 flex items-center gap-2 text-[10px] text-gray-400">
                <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px]">↑↓</kbd> naviguer
                <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px]">↵</kbd> ouvrir
                <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px]">Esc</kbd> fermer
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
